import { useState } from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import { PRIMARY_NAV } from '../../config/navConfig';

type Props = {
  active: string;
  go: (path: string) => void;
};

const MAIN_LABELS = ['Home', 'Notes', 'Payments', 'Grocery', 'Chat', 'Tunes', 'Videos'];
const MAIN_NAV = PRIMARY_NAV.filter((n) => MAIN_LABELS.includes(n.label));
const MORE_NAV = PRIMARY_NAV.filter((n) => !MAIN_LABELS.includes(n.label));

const itemSx = (isActive: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: 0.85,
  borderRadius: '999px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  fontSize: 14,
  color: isActive ? '#fff' : 'text.secondary',
  background: isActive ? 'linear-gradient(135deg, var(--qs-a, #6226A7), var(--qs-b, #F6A938))' : 'transparent',
  backgroundSize: '160% 160%',
  fontWeight: isActive ? 700 : 500,
  boxShadow: isActive ? '0 6px 16px rgba(98,38,167,0.4), inset 0 1px 0 rgba(255,255,255,.45)' : 'none',
  transition: 'background .15s, color .15s, transform .15s, box-shadow .15s',
  '&:hover': {
    bgcolor: isActive ? undefined : 'action.hover',
    transform: 'translateY(-1px)',
  },
});

const HorizontalNav = ({ active, go }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const moreActive = MORE_NAV.some((n) => active.startsWith(n.matchPrefix));

  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {MAIN_NAV.map(({ label, path, icon: Icon, matchPrefix }) => (
        <Box
          key={path}
          role="link"
          tabIndex={0}
          onClick={() => go(path)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') go(path); }}
          sx={itemSx(active.startsWith(matchPrefix))}
        >
          <Icon fontSize="small" />
          {label}
        </Box>
      ))}

      <Box
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        onClick={(e) => setAnchor(e.currentTarget)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAnchor(e.currentTarget as HTMLElement); }}
        sx={itemSx(moreActive)}
      >
        <MoreHorizRounded fontSize="small" />
        More
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {MORE_NAV.map(({ label, path, icon: Icon, matchPrefix }) => (
          <MenuItem
            key={path}
            selected={active.startsWith(matchPrefix)}
            onClick={() => { setAnchor(null); go(path); }}
          >
            <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default HorizontalNav;
